<template>
  <div class="offers">
    <v-layout row wrap>
      <component
        :is="templateKey"
        :agreement="agreement"
        :agreementDisplayLabel="agreementDisplayLabel"
        :requestAgreementLoading="requestAgreementLoading"
        :showOptInBox="showOptInBox"
        :showLegalTextBelowContinueButton="showLegalTextBelowContinueButton"
        :showTCPALegal="showTCPALegal"
        v-model="model"
        :acceptTCPALegal="acceptTCPALegal"
        :truncatedLegalTextEnabled="truncatedLegalTextEnabled"
        :formValid="formValid"
        @phoneEdited="editedPhone"
        @verticalsEvent="handleVerticals"
        @tcpaLegalEvent="handleTCPALegal"
        @submit="handleSubmit"></component>
      <div v-if="Object.keys(agreement).length === 0" class="no-offers-container">
        <div class="no-offers-container__info">
          <img
            alt="info-icon"
            src="../../assets/icons/info.svg"
            height="24px"
            width="24px"
            contain
            class="info-img"/>
          <div>
            We're sorry about this, but we currently do not have any offers available to you at
            this time from our partners.
          </div>
        </div>
        <div class="no-offers-container__continue">
          Please continue to view your HomeGauge account.
        </div>
        <div class="no-offers-container__my-offers">
          You can check the <b>My Offers</b> page for updated discounts or resources to get you
          settled into your new home. As always, these are entirely optional to you!
        </div>
      </div>
    </v-layout>
  </div>
</template>

<script>
import OffersClient from '@/utilities/offers-client';
import WelcomeOffersTemplates from '@/components/Welcome/OffersTemplates';
import { mapGetters, mapState } from 'vuex';
import { TCPAAreaCodes, TCPA_OUTCOMES } from '@/utilities/constants';
import Jornaya from '@/utilities/jornaya';
import FF from '../../constants/feature-flags';

// Linter complains about using the literal string, fix is putting into a var
const ON_FORM_CHANGE = 'on-form-change';
const ON_VALIDATION_CHANGE = 'on-validation-change';

export default {
  components: {
    ...WelcomeOffersTemplates,
  },
  props: {
    formValid: {
      type: Boolean,
      required: false,
    },
  },
  data() {
    return {
      clients: {},
      agreement: {},
      requestAgreementLoading: false,
      phoneTypes: ['Work', 'Mobile', 'Home'],
      model: {
        phoneType: '',
        phoneNumber: '',
      },
      acceptTCPALegal: false,
      areaCode: {
        state: '',
        code: '',
      },
      latestInspectionState: '',
      showTCPALegal: false,
      TCPALegalHasBeenShowedOneTime: false,
      phoneNumberDirty: false,
      templateKey: 'frida',
      jornaya: null,
    };
  },
  computed: {
    ...mapGetters('User', ['userAuthToken']),
    ...mapGetters('Inspection', ['latestInspectionAddress']),
    ...mapState('unleash', {
      enabledFeatures: state => state.enabledFeatures,
    }),
    tCPALegalEnabled() {
      return this.enabledFeatures[FF.HG_1021_STATE_TCPA_WELCOME_FLOW];
    },
    showLegalTextBelowContinueButton() {
      return this.enabledFeatures['HG-377-Move-Legal-Text'];
    },
    truncatedLegalTextEnabled() {
      return this.enabledFeatures['HG-380-Truncate-Legal-Text'];
    },
    agreementSelected() {
      let isSelected = false;
      if (!this.agreement.offers?.length) {
        return isSelected;
      }
      this.agreement.offers.forEach(element => {
        if (element.selected) {
          isSelected = true;
        }
      });
      return isSelected;
    },
    existingPhone() {
      const { currentUser } = this.$store.state.User;
      return currentUser.workPhone || currentUser.homePhone || currentUser.mobilePhone;
    },
    agreementDisplayLabel() {
      return this.agreement.displayLabel ? this.agreement.displayLabel : 'Agreements and Offers';
    },
    showOptInBox() {
      return (
        this.agreementSelected
        && (this.existingPhone === '' || this.phoneNumberDirty
        || (this.tCPALegalEnabled && (this.parsedStateisTCPA() || this.parsedAreaCodeisFL())))
      );
    },
  },
  methods: {
    async initialHydration() {
      this.jornaya = Jornaya.getInstance();
      this.emitValidationChange();
      this.initializeClients();
      const { currentUser } = this.$store.state.User;
      let phone = '';
      // eslint-disable-next-line no-nested-ternary
      phone = currentUser.workPhone
        ? this.phoneTypes[0]
        : currentUser.homePhone
          ? this.phoneTypes[2]
          : this.phoneTypes[1];
      this.model = {
        phoneType: phone,
        phoneNumber: currentUser.workPhone || currentUser.homePhone || currentUser.mobilePhone,
      };
      if (this.model.phoneNumber && this.tCPALegalEnabled) {
        const areaCodeDigits = this.model.phoneNumber.slice(0, 3);
        this.parseAreaCodeFromDigits(areaCodeDigits);
      }
      if (this.model.phoneNumber) {
        this.model.phoneNumber = this.model.phoneNumber.replace(/^\+1/, '');
      }
      if (this.model.phoneNumber && this.model.phoneNumber.startsWith('1')) {
        this.model.phoneNumber = this.model.phoneNumber.substr(1);
      }
      const folder = await this.retrieveInspections();
      this.requestAgreementLoading = true;
      const { verticalAgreements, templateKey } = await this.requestAgreement(folder);
      this.templateKey = templateKey;
      this.$store.dispatch('Folders/setTemplateKey', templateKey);
      this.$store.dispatch('Folders/setAgreements', verticalAgreements);
      if (verticalAgreements) {
        const [agreement] = verticalAgreements;
        this.agreement = {
          ...agreement,
          displayText: this.replaceAgreementTextVars(
            agreement.displayText,
            {
              ...this.$store.state.User.currentUser,
              inspectionAddress: this.latestInspectionAddress,
            },
          ),
        };
        this.agreement.offers.map(offer => ({
          ...offer,
          selected: false,
        }));
      }
      this.requestAgreementLoading = false;
    },
    emitValidationChange() {
      this.$emit(ON_VALIDATION_CHANGE, true);
    },
    requestAgreement(folder) {
      const { postalCode, state, country } = this.$store.state.Inspection.inspections[0];
      const caseInsensitiveCountry = country?.toLowerCase();
      if (country && (caseInsensitiveCountry !== 'us' && caseInsensitiveCountry !== 'un')) {
        return {};
      }
      const requestAgreementParams = {
        location: 'welcome',
        includeTemplateKey: true,
        companyId: folder.companyId,
        companyGuid: folder.companyUuid,
        postalCode,
        state,
      };
      return this.clients.offersClient.getAgreements(requestAgreementParams);
    },
    initializeClients() {
      this.clients.offersClient = new OffersClient(this.userAuthToken);
    },
    async retrieveInspections() {
      const { currentUser } = this.$store.state.User;
      const InspectionsResponse = await this.$store.dispatch(
        'Inspection/loadInspections',
        currentUser.legacyId,
      );
      if (
        InspectionsResponse
        && InspectionsResponse.folders
        && InspectionsResponse.folders.length > 0
      ) {
        return InspectionsResponse.folders[0];
      }
      return '';
    },
    parseStateFromMostRecentInspection() {
      const inspectionExist = this.$store.state.Inspection.inspections.length > 0;
      if (!inspectionExist) {
        this.latestInspectionState = '';
      } else {
        const latestInspection = this.$store.state.Inspection.inspections[0];
        const { state } = latestInspection;
        this.latestInspectionState = state;
      }
      this.showTCPALegal = this.parsedStateisTCPA();
      if (!this.showTCPALegal) {
        this.showTCPALegal = this.parsedAreaCodeisFL();
      }
    },
    parseAreaCodeFromDigits(areaCodeDigits) {
      const foundArea = TCPAAreaCodes.find(state => state.areaCodes.includes(areaCodeDigits));
      this.areaCode = foundArea?.state ? { ...foundArea } : {};
    },
    parsedStateisTCPA() {
      const isTCPA = TCPAAreaCodes.find(area => area.state === this.latestInspectionState);
      return !!isTCPA;
    },
    parsedAreaCodeisFL() {
      return this.areaCode.state === 'FL';
    },
    shouldEmitTCPAoutcome(phoneNumber) {
      if (!this.showTCPALegal && phoneNumber.length === 10) {
        this.$emit(ON_FORM_CHANGE, { tcpa_outcome: TCPA_OUTCOMES.CHANGED_PHONE });
      }
      if (phoneNumber.length === 0) {
        this.$emit(ON_FORM_CHANGE, { tcpa_outcome: TCPA_OUTCOMES.CLEARED_PHONE });
      }
    },
    setTCPALegalHasBeenShowedOneTime() {
      if (!this.TCPALegalHasBeenShowedOneTime) {
        this.TCPALegalHasBeenShowedOneTime = true;
        this.$emit(ON_FORM_CHANGE, { tcpa_shown: true });
      }
    },
    shouldShowTCPALegal(stateIsTCPA, areaCodeFL, legalHasBeenShowed) {
      /* eslint-disable no-lonely-if */
      if (!stateIsTCPA && areaCodeFL) {
        this.showTCPALegal = true;
        if (legalHasBeenShowed) {
          this.setTCPALegalHasBeenShowedOneTime();
        }
      }
    },
    editedPhone() {
      this.phoneNumberDirty = true;
    },
    handleVerticals(value) {
      /* eslint-disable no-param-reassign */
      this.agreement.offers = this.agreement.offers.map(offer => ({
        ...offer,
        selected: value,
      }));
    },
    handleTCPALegal(value) {
      this.acceptTCPALegal = value ?? false;
    },
    handleSubmit() {
      this.$emit('submit');
    },
    replaceAgreementTextVars(agreementText, userObj) {
      let replacedText = agreementText;
      const userObjectEntries = Object.entries(userObj);
      userObjectEntries.forEach(([key, value]) => {
        if (value && typeof value === 'string') {
          replacedText = replacedText.replace(`{{${key}}}`, value);
        } else if (value && typeof value === 'object') {
          Object.entries(value).forEach(([subKey, subValue]) => {
            replacedText = replacedText.replace(`{{${key}.${subKey}}}`, subValue);
          });
        }
      });
      return replacedText;
    },
  },
  watch: {
    agreement: {
      handler(value) {
        const verticalIdsArrays = value.offers
          .filter(offer => offer.selected)
          .map(offer => offer.verticalIds);

        const verticalIds = Array.prototype.concat.apply([], verticalIdsArrays);
        this.$emit(ON_FORM_CHANGE, { agreement: { [value.agreementId]: verticalIds } });
      },
      deep: true,
    },
    model: {
      handler(value) {
        this.$emit(ON_FORM_CHANGE, value);
        if (this.tCPALegalEnabled) {
          const { phoneNumber } = value;
          const parsedStateisTCPA = this.parsedStateisTCPA();
          if (phoneNumber.length > 2) {
            const areaCodeDigits = phoneNumber.slice(0, 3);
            this.parseAreaCodeFromDigits(areaCodeDigits);
            this.shouldShowTCPALegal(
              parsedStateisTCPA,
              this.parsedAreaCodeisFL(),
              this.TCPALegalHasBeenShowedOneTime,
            );
          } else {
            this.showTCPALegal = parsedStateisTCPA;
            if (!this.showTCPALegal) {
              this.acceptTCPALegal = false;
            }
          }
          if (this.TCPALegalHasBeenShowedOneTime) {
            this.shouldEmitTCPAoutcome(phoneNumber);
          }
        }
      },
      deep: true,
    },
    showOptInBox: {
      handler(value) {
        if (this.showTCPALegal && value && !this.TCPALegalHasBeenShowedOneTime) {
          // We don't want to turn this off once it it on
          this.setTCPALegalHasBeenShowedOneTime();
          this.$emit(ON_VALIDATION_CHANGE, false);
        }
        if (!value && this.TCPALegalHasBeenShowedOneTime) {
          this.$emit(ON_FORM_CHANGE, { tcpa_outcome: TCPA_OUTCOMES.OPT_OUT });
          this.acceptTCPALegal = false;
          this.$emit(ON_VALIDATION_CHANGE, true);
        }
      },
      deep: true,
    },
    acceptTCPALegal: {
      handler(value) {
        if (value && this.TCPALegalHasBeenShowedOneTime) {
          this.$emit(ON_FORM_CHANGE, { tcpa_outcome: TCPA_OUTCOMES.OPT_IN });
        }
      },
      deep: true,
    },
  },
  async mounted() {
    await this.initialHydration();
    if (this.tCPALegalEnabled) {
      this.parseStateFromMostRecentInspection();
      this.parsedAreaCodeisFL();
    }
  },
};
</script>

<style lang="scss" scoped>
.no-offers-container {
    padding-top: 24px;
    color: #2d2d2d;
    font-family: Montserrat;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.75;
    &__info {
      background-color: #dff0ff;
      display: flex;
      padding: 16px;
      font-size: 16px;

      & .info-img {
        margin-right: 16px;
        margin-top: 6px;
      }
    }

    &__continue {
      padding-top: 24px;
    }

    &__my-offers {
      padding: 24px 0 32px 0;
    }
  }
</style>
